<template>
  <b-overlay :show="isLoading" rounded="sm">
    <template #overlay>
      <div class="text-center">
        <digger-loader />
      </div>
    </template>

    <div class="p-relative header-image pb-5"></div>

    <b-container class="custom-container mt-5">
      <b-row>
        <b-col cols="12" class="px-3">
          <pending-token-banner @review="goToInProgress" class="mb-3" />
          <candidates-tabs />

          <h4 class="font-weight-bold pt-4 pb-1 align-middle">Discover</h4>
          <p class="text-muted mb-4">
            Browse tradies who are currently available and looking for work.
          </p>

          <candidates-dashboard-stats class="mb-4" />
          <hr class="candidates-section-divider" />

          <b-row class="candidates-layout-row mt-4">
            <b-col cols="12" md="4" class="mb-4 candidates-sidebar-col">
              <candidate-filter-sidebar
                :trade-options="tradeOptions"
                :location-options="locationOptions"
                @changed="onFiltersChanged"
              />
            </b-col>

            <b-col cols="12" md="8" class="candidates-grid-col">
              <b-row
                v-if="candidates.length"
                class="align-items-stretch candidates-grid-row"
              >
                <b-col
                  v-for="candidate in candidates"
                  :key="candidate.id"
                  cols="12"
                  sm="6"
                  lg="4"
                  class="mb-4"
                >
                  <candidate-card
                    :candidate="candidate"
                    :show-workforce-invite="false"
                    plain-files
                    @view="goToCandidate"
                    @request-resume="openResumeRequestModal"
                    @error="onError"
                  />
                </b-col>
              </b-row>

              <p v-else-if="!isLoading" class="text-muted py-5 text-center">
                No candidates found for these filters.
              </p>

              <b-pagination
                v-if="candidatesMeta && candidatesMeta.total > perPage"
                v-model="currentPage"
                :total-rows="candidatesMeta.total"
                :per-page="perPage"
                align="center"
                class="mt-3"
                @change="onPageChanged"
              />
            </b-col>
          </b-row>
        </b-col>
      </b-row>
    </b-container>

    <resume-request-modal
      :showModal="isRequestingResume"
      :candidate="candidateForModal"
      @closeModal="isRequestingResume = false"
      @sent="onResumeRequestSent"
    />
  </b-overlay>
</template>

<script>
import { mapActions, mapGetters } from 'vuex'
import CandidateFilterSidebar from '@/components/candidates/CandidateFilterSidebar.vue'
import CandidateCard from '@/components/candidates/CandidateCard.vue'
import CandidatesTabs from '@/components/candidates/CandidatesTabs.vue'
import CandidatesDashboardStats from '@/components/candidates/CandidatesDashboardStats.vue'
import PendingTokenBanner from '@/components/candidates/PendingTokenBanner.vue'
import ResumeRequestModal from '@/components/modals/ResumeRequestModal.vue'

// Location options — there's no location catalogue endpoint (the API just
// stores `preferred_locations` as free-text strings, no enum), so this is
// the canonical list until the mobile-side profile form exists to write
// against it. Australia-wide by state rather than WA regions, since nothing
// in the product actually restricts Open to Work to WA.
const DEFAULT_LOCATION_OPTIONS = [
  { id: 'Australian Capital Territory', label: 'Australian Capital Territory' },
  { id: 'New South Wales', label: 'New South Wales' },
  { id: 'Northern Territory', label: 'Northern Territory' },
  { id: 'Queensland', label: 'Queensland' },
  { id: 'South Australia', label: 'South Australia' },
  { id: 'Tasmania', label: 'Tasmania' },
  { id: 'Victoria', label: 'Victoria' },
  { id: 'Western Australia', label: 'Western Australia' },
]

export default {
  name: 'CandidatesListPage',

  components: {
    CandidateFilterSidebar,
    CandidateCard,
    CandidatesTabs,
    CandidatesDashboardStats,
    PendingTokenBanner,
    ResumeRequestModal,
  },

  data() {
    return {
      isLoading: false,
      currentPage: 1,
      perPage: 18,
      currentFilters: {},
      locationOptions: DEFAULT_LOCATION_OPTIONS,
      // Real trade values on file for visible candidates — not the app-wide
      // "official" trade picker list, which won't include free-text values a
      // tradie typed before trade was a proper dropdown. See
      // fetchCandidateTradeOptions() below.
      tradeOptions: [],

      isRequestingResume: false,
      candidateForModal: null,
    }
  },

  computed: {
    ...mapGetters('candidate', ['candidates', 'candidatesMeta']),
  },

  async created() {
    this.loadTradeOptions()
    await this.loadCandidates()
  },

  methods: {
    ...mapActions('candidate', [
      'fetchCandidates',
      'fetchCandidateTradeOptions',
    ]),

    async loadTradeOptions() {
      try {
        const trades = await this.fetchCandidateTradeOptions()
        this.tradeOptions = trades.map((trade) => ({ id: trade, label: trade }))
      } catch (err) {
        // Non-critical filter — leave it empty rather than surface an error
        // toast for it (matches CandidateFilterSidebar's own ticket-options
        // fetch, which does the same).
      }
    },

    async loadCandidates() {
      this.isLoading = true
      try {
        await this.fetchCandidates({
          filters: this.currentFilters,
          page: this.currentPage,
          perPage: this.perPage,
        })
      } catch (err) {
        this.onError(err)
      } finally {
        this.isLoading = false
      }
    },

    onFiltersChanged(filters) {
      this.currentFilters = filters
      this.currentPage = 1
      this.loadCandidates()
    },

    onPageChanged(page) {
      this.currentPage = page
      this.loadCandidates()
    },

    goToCandidate(id) {
      this.$router.push({
        name: 'candidates.view-candidate',
        params: { candidateId: id },
      })
    },

    goToInProgress() {
      this.$router.push({ name: 'candidates.in-progress-page' }).catch(() => {})
    },

    openResumeRequestModal(tradieId) {
      this.candidateForModal = this.candidates.find(
        (c) => c.id === tradieId,
      ) || {
        id: tradieId,
      }
      this.isRequestingResume = true
    },

    onResumeRequestSent() {
      this.isRequestingResume = false
      this.showToast('Success', 'Resume request sent.')
      this.loadCandidates()
    },

    onError(err) {
      const message =
        (err && err.data && err.data.message) ||
        (err && err.message) ||
        'Something went wrong.'
      this.showToast('Error', message, 'danger')
    },

    showToast(title, message, variant = 'success') {
      this.$bvToast.toast(message, {
        title,
        autoHideDelay: 3000,
        variant,
      })
    },
  },
}
</script>

<style lang="scss" scoped>
// The dashboard stat tiles and the browse grid below are both white cards on
// the same light-grey page background, sitting close enough that they read
// as one merged block. A full-width rule with real breathing room on both
// sides makes clear they're two separate sections.
.candidates-section-divider {
  margin: 0 0 1.5rem;
  border-top: 1px solid #e1e6ea;
}

// Same `.col-12` bleed as `.candidates-grid-row` below — both
// candidates-sidebar-col and candidates-grid-col carry the base `col-12`
// class (col-md-4/8 only overrides width from 768px up, not the class
// list), so they're force-zeroed to 0 side padding by that same global
// rule and the row's default -15px margins bled straight past the page's
// own padding, flush against the viewport edge. Zeroing the row's margins
// stops the bleed; explicit padding on both outer edges (sidebar's left,
// grid's right) restores real breathing room there, and a smaller amount
// on the touching inner edges gives a deliberate but not oversized gap
// between the filter panel and the results (rather than a flex `gap`,
// which would add to — not eat into — the column widths and push the grid
// onto its own line). Below md they stack full-width and the ancestor
// col-12's own `px-3` already covers the outer edges, so this only
// applies once the two-column split is active.
.candidates-layout-row {
  margin-left: 0;
  margin-right: 0;

  @media (min-width: 768px) {
    > .candidates-sidebar-col {
      padding-left: 1rem;
      padding-right: 1rem;
    }

    > .candidates-grid-col {
      padding-left: 1rem;
      padding-right: 1rem;
    }
  }
}

// Bootstrap's default .row carries -15px side margins, meant to be soaked up
// by the 15px side padding on a normal parent column. This grid's parent col
// is a global `.col-12` (see src/assets/scss/base/_animations.scss), which
// is force-zeroed to 0 side padding, so the row's negative margins bleed
// 15px past the viewport edge with nothing to cancel them — invisible on a
// wide desktop screen (silently clipped, no visible scrollbar) but a real
// horizontal-scroll bug from ~375px down to 320px. Zeroing the row's own
// margins removes the bleed at every width without touching the column
// padding rules above (which govern the sidebar/grid gap, not this).
.candidates-grid-row {
  margin-left: 0;
  margin-right: 0;
}
</style>
